import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth, UserRoleEnum } from './AuthContext';
import { Star, Users, Globe, Shield, Heart, Zap, LayoutDashboard, LogOut, Check } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import AppHeader from './AppHeader';
import { Plan as BasePlan, UserType, BillingType } from '../types/plan';
import { PlanService } from '../services/PlanService';
import { PlanAccessValidator } from '../services/PlanAccessValidator';
import InfoModal from './InfoModal';

enum PlanSection {
  TERAPEUTA = 'terapeuta',
  PARTICIPANTE = 'participante'
}

// Interface estendida para compatibilidade com código legado
interface Plan extends BasePlan {
  type?: 'terapeuta' | 'participante';
  title?: string;
  price?: string;
  originalPrice?: string;
  period?: string;
  redirectTo?: string;
  features?: string[];
  isClubPlan?: boolean;
  requiresScheduling?: boolean;
  actualPrice?: number;
}

// --- Componente do Card (Design Atualizado) ---
const PlanCard: React.FC<{ plan: Plan; onSelect: (id: string) => void; isLoading: boolean }> = ({ plan, onSelect, isLoading }) => {
  // Determina o texto do botão baseado no tipo de plano
  const buttonText = PlanService.getButtonText(plan.isRecurring);

  const formatPeriodForDisplay = (period: string) => {
    return period
      .toUpperCase()
      .replace('/MÊS', ' POR<br/>MÊS')
      .replace('/DIA', ' POR<br/>DIA')
      .replace('POR VIVÊNCIA', 'POR<br/>VIVÊNCIA')
      .replace('POR MÊS', 'POR<br/>MÊS')
      .replace('SESSÃO COMPLETA', 'SESSÃO<br/>COMPLETA');
  };

  return (
    <div className={`flex flex-col rounded-3xl shadow-2xl transform hover:-translate-y-2 transition-transform duration-300 text-white relative ${plan.isFeatured ? 'bg-gradient-to-b from-[#7ED99C] to-[#5BB77E] ring-4 ring-[#FFD700] pt-12 pb-8 px-8' : 'bg-gradient-to-b from-[#5C528A] to-[#3A3157] p-8'}`}>
      {plan.isFeatured && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#FFD700] text-[#3A3157] px-6 py-2 rounded-full font-bold text-sm uppercase shadow-lg z-10">
          ⭐ Mais Popular
        </div>
      )}
      <h3 className="text-3xl font-bold uppercase tracking-wider text-center mb-6">{plan.title}</h3>

      <div className="text-center mb-6 min-h-[80px] flex flex-col items-center justify-center">
        {plan.originalPrice ? (
          <div className="flex flex-col items-center justify-center gap-2">
            <div className="text-center leading-none">
              <span className="text-lg line-through opacity-80">DE {plan.originalPrice}</span><br/>
              <span className="text-lg">POR</span> <span className="text-5xl font-bold">{plan.price}</span>
            </div>
            <div className="text-md text-center font-semibold opacity-90 leading-tight" dangerouslySetInnerHTML={{ __html: formatPeriodForDisplay(plan.period || '') }} />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2">
            <span className="text-6xl font-bold">{plan.price}</span>
            <div className="text-md text-center font-semibold opacity-90 leading-tight" dangerouslySetInnerHTML={{ __html: formatPeriodForDisplay(plan.period || '') }} />
          </div>
        )}
      </div>

      <div className="text-2xl text-white text-center font-light leading-tight mb-8 flex-grow">
        <p className="whitespace-pre-line">{plan.description}</p>
      </div>
      
      {plan.features && plan.features.length > 0 && (
        <ul className="mb-8 space-y-3 text-base flex-grow text-left">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 stroke-[3] ${plan.isFeatured ? 'text-[#5C528A]' : 'text-[#7ED99C]'}`} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={() => onSelect(plan.id || '')}
        disabled={isLoading}
        className={`w-full mt-auto py-4 px-6 font-bold text-lg rounded-full transition-all duration-300 disabled:opacity-60 disabled:cursor-wait uppercase tracking-wider shadow-lg hover:shadow-xl transform hover:scale-105 ${plan.isFeatured ? 'bg-gradient-to-r from-[#5C528A] to-[#3A3157] text-white' : 'bg-gradient-to-r from-[#80E29E] to-[#63C783] text-[#3A3157]'}`}
      >
        {isLoading ? 'Aguarde...' : buttonText}
      </button>
    </div>
  );
};


// --- Componente Principal ---
export default function Plans() {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [processingPlan, setProcessingPlan] = React.useState<string | null>(null);
  const [dynamicTherapistPlans, setDynamicTherapistPlans] = useState<Plan[]>([]);
  const [dynamicParticipantPlans, setDynamicParticipantPlans] = useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  
  // Estado do modal
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'warning' as 'error' | 'warning' | 'info' | 'success',
    redirectTo: ''
  });

  // Extrair parâmetro da URL para determinar seção específica
  const searchParams = new URLSearchParams(location.search);
  const querySectionRaw = searchParams.get('secao');
  const specificSection: PlanSection | null =
    querySectionRaw === PlanSection.TERAPEUTA
      ? PlanSection.TERAPEUTA
      : querySectionRaw === PlanSection.PARTICIPANTE
        ? PlanSection.PARTICIPANTE
        : null;

  // Se não vier ?secao na URL, usa o tipo do usuário logado para definir a seção
  // IMPORTANTE: Terapeutas não têm seção padrão (veem tudo)
  const roleBasedSection: PlanSection | null = React.useMemo(() => {
    if (!isAuthenticated || !user?.role) return null;
    // Terapeutas não têm seção efetiva por padrão - veem todos os planos
    if (user.role === UserRoleEnum.TERAPEUTA) return null;
    // Pacientes/participantes veem apenas planos de participante
    if (user.role === UserRoleEnum.PARTICIPANTE || user.role === UserRoleEnum.PACIENTE) return PlanSection.PARTICIPANTE;
    return null;
  }, [isAuthenticated, user?.role]);

  const effectiveSection: PlanSection | null = specificSection ?? roleBasedSection;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Buscar planos dinâmicos do Firestore
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const allPlans = await PlanService.fetchActivePlans(db);
        // Planos de terapeutas
        const therapistPlans: Plan[] = allPlans.filter(plan => plan.userType === UserType.THERAPIST).map((basePlan) => ({
          ...basePlan,
          type: 'terapeuta' as const,
          title: basePlan.name,
          price: PlanService.formatCurrency(basePlan.value),
          period: PlanService.formatPeriod(basePlan.quantity, basePlan.billingType, basePlan.isRecurring),
          requiresScheduling: false,
          actualPrice: basePlan.value
        }));
        therapistPlans.sort((a, b) => (a.value || 0) - (b.value || 0));
        setDynamicTherapistPlans(therapistPlans);
        // Planos de participantes
        const participantPlans: Plan[] = allPlans.filter(plan => plan.userType === UserType.PATIENT).map((basePlan) => ({
          ...basePlan,
          type: 'participante' as const,
          title: basePlan.name,
          price: PlanService.formatCurrency(basePlan.value),
          period: PlanService.formatPeriod(basePlan.quantity, basePlan.billingType, basePlan.isRecurring),
          requiresScheduling: false,
          actualPrice: basePlan.value
        }));
        participantPlans.sort((a, b) => (a.value || 0) - (b.value || 0));
        setDynamicParticipantPlans(participantPlans);
      } catch (error) {
        console.error('Erro ao buscar planos:', error);
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  // --- FLUXO DE COMPRA CONTÍNUO: useEffect para processar compra pós-login ---
  React.useEffect(() => {
    // Verifica se o usuário acabou de fazer login e tem um plano para comprar
    if (isAuthenticated && location.state?.planIdToPurchase) {
      const planIdToPurchase = location.state.planIdToPurchase;
      
      // Limpa o estado da navegação para evitar reexecução
      navigate(location.pathname, { replace: true, state: {} });
      
      // Inicia o checkout do plano selecionado
      handlePlanSelection(planIdToPurchase);
    }
  }, [isAuthenticated, location.state?.planIdToPurchase, navigate]);

  // Função para obter o preço numérico de uma string de preço
  const getNumericPrice = (priceString: string): number => {
    // Remove "R$", espaços, e pontos, substitui vírgula por ponto
    const cleanPrice = priceString.replace(/[R$\s.]/g, '').replace(',', '.');
    return parseFloat(cleanPrice);
  };

  // Função para encontrar um plano pelo ID
  const findPlanById = (planId: string): Plan | null => {
    const allPlans = [...dynamicTherapistPlans, ...dynamicParticipantPlans];
    return allPlans.find(plan => plan.id === planId) || null;
  };

  const handlePlanSelection = async (planId: string) => {
    if (!isAuthenticated) {
      // --- FLUXO DE COMPRA CONTÍNUO: Redireciona para login com informação do plano ---
      navigate('/login', { 
        state: { 
          from: location, 
          planId: planId 
        } 
      });
      return;
    }

    const selectedPlan = findPlanById(planId);
    if (!selectedPlan) {
      console.error('Plano não encontrado:', planId);
      return;
    }

    // Validar se o usuário pode acessar este plano
    if (user && user.role && selectedPlan.userType) {
      const validation = PlanAccessValidator.canAccessPlan(user.role, selectedPlan.userType);
      
      if (!validation.canAccess) {
        setModalState({
          isOpen: true,
          title: validation.title || 'Acesso Negado',
          message: validation.message || '',
          variant: validation.variant || 'warning',
          redirectTo: validation.redirectTo || ''
        });
        return;
      }
    }

    setProcessingPlan(planId);

    try {
      if (selectedPlan.redirectTo) {
        navigate(selectedPlan.redirectTo);
        return;
      }

      // Se o plano requer agendamento, redireciona para a página de agendamento
      if (selectedPlan.requiresScheduling) {
        navigate(`/agendamento?plan=${planId}`);
        return;
      }

      // Redirecionar para a página de checkout com o ID do plano
      navigate(`/checkout?planId=${planId}`);

    } catch (error) {
      console.error('Erro ao processar seleção do plano:', error);
      setModalState({
        isOpen: true,
        title: 'Erro',
        message: 'Erro ao processar sua solicitação. Por favor, tente novamente.',
        variant: 'error',
        redirectTo: ''
      });
    } finally {
      setProcessingPlan(null);
    }
  };
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const shouldShowSection = (requiredRole: PlanSection) => {
    // Se há uma seção efetiva via URL
    if (effectiveSection) {
      // Se a URL pede seção de terapeuta, mostra TODAS as seções (comportamento especial)
      if (effectiveSection === PlanSection.TERAPEUTA) {
        return true; // Mostra tanto terapeuta quanto participante
      }
      // Se a URL pede seção de participante, mostra apenas participante
      return effectiveSection === requiredRole;
    }

    // Lógica sem seção específica: terapeutas veem tudo, pacientes só veem planos de paciente
    
    // Administradores veem tudo
    if (isAuthenticated && user?.role === UserRoleEnum.ADMINISTRADOR) return true;

    // Para a seção de terapeutas:
    if (requiredRole === PlanSection.TERAPEUTA) {
      // Apenas terapeutas veem planos de terapeuta
      return isAuthenticated && user?.role === UserRoleEnum.TERAPEUTA;
    }
    
    // Para a seção de participantes:
    if (requiredRole === PlanSection.PARTICIPANTE) {
      // Se não está autenticado, mostra a seção
      if (!isAuthenticated) return true;
      
      // Se o usuário tem role "login-not-completed", trata como não logado
      if (user?.role === UserRoleEnum.LOGIN_NOT_COMPLETED) return true;
      
      // Terapeutas também podem ver planos de participantes
      if (user?.role === UserRoleEnum.TERAPEUTA) return true;
      
      // Pacientes e participantes veem planos de participantes
      return user?.role === UserRoleEnum.PARTICIPANTE || user?.role === UserRoleEnum.PACIENTE;
    }
    
    return false;
  };

  // Controlar visibilidade da seção de benefícios baseado no role do usuário
  const shouldShowTherapistBenefits = React.useMemo(() => {
    // Se há seção específica na URL, usa ela
    if (effectiveSection === PlanSection.TERAPEUTA) return true;
    if (effectiveSection === PlanSection.PARTICIPANTE) return false;
    
    // Se não há seção específica, baseia-se no role do usuário
    return isAuthenticated && user?.role === UserRoleEnum.TERAPEUTA;
  }, [effectiveSection, isAuthenticated, user?.role]);

  const getPageTitle = () => {
    // Se há seção específica na URL
    if (effectiveSection === PlanSection.TERAPEUTA) {
      return "Para Consteladores";
    }
    if (effectiveSection === PlanSection.PARTICIPANTE) {
      return "Conheça as Constelações";
    }

    // Lógica baseada no role do usuário
    if (!isAuthenticated) return "CONSTELA BRASIL";
    if (user?.role === UserRoleEnum.ADMINISTRADOR) return "Todos os Planos Disponíveis";
    if (user?.role === UserRoleEnum.TERAPEUTA) return "Para Consteladores";
    return "Conheça as Contelações";
  };

  const getPageSubtitle = () => {
    // Se há seção específica na URL e é terapeuta
    if (effectiveSection === PlanSection.TERAPEUTA) {
      return "Expanda seu trabalho para todo o Brasil com nossa plataforma de constelação virtual";
    }
    if (effectiveSection === PlanSection.PARTICIPANTE) {
      return "A Primeira Plataforma de Constelação Familiar em grupo Online com a mesma profundidade do presencial";
    }

    // Lógica baseada no role do usuário
    if (!isAuthenticated) return "A Primeira Plataforma de Constelação Familiar em grupo Online com a mesma profundidade do presencial";
    if (user?.role === UserRoleEnum.TERAPEUTA) return "Expanda seu trabalho para todo o Brasil com nossa plataforma de constelação virtual";
    return "";
  };

  return (
    <div className="bg-[#EAF4F2] min-h-screen">
      <AppHeader />

      <div className="pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="container mx-auto px-4">
          <header className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#4A7C75' }}>
              {getPageTitle()}
            </h1>
            {getPageSubtitle() && (
              <>
                <p className="text-lg md:text-xl max-w-4xl mx-auto mb-6 font-semibold" style={{ color: '#6C4E78' }}>
                  {getPageSubtitle()}
                </p>
                <div className="flex items-center justify-center gap-2 mb-12">
                  <Star className="h-5 w-5" style={{ color: '#7ED99C' }} />
                  <p className="text-base md:text-lg italic" style={{ color: '#4A7C75' }}>
                    {effectiveSection === PlanSection.TERAPEUTA 
                      ? "Planos para Consteladores expandirem seus atendimentos online"
                      : "Para Consteladores, para Quem Quer Fazer a sua Constelação, Representar e participar ou interessados no assunto"
                    }
                  </p>
                </div>
              </>
            )}
          </header>

          {/* Benefícios para Participantes - Só mostra se não for terapeuta */}
          {!shouldShowTherapistBenefits && (
            <section className="mb-20">
              <div className="max-w-6xl mx-auto">
                <div className="mb-16">
                  <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#6C4E78' }}>
                    Participe Como <span style={{ color:'#7ED99C'}}>representante</span> ou faça a sua <span style={{ color:'#7ED99C'}}>Constelação Familiar</span> com <span style={{ color:'#7ED99C'}}>profissionais homologados</span> pela plataforma.
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-white rounded-2xl p-6 shadow-md text-center">
                      <Globe className="h-8 w-8 mx-auto mb-4" style={{ color: '#7ED99C' }} />
                      <h3 className="text-lg font-semibold mb-2" style={{ color: '#6C4E78' }}>Experiência igual à presencial</h3>
                      <p className="text-sm" style={{ color: '#4A7C75' }}>Mas sem precisar se deslocar</p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-md text-center">
                      <Zap className="h-8 w-8 mx-auto mb-4" style={{ color: '#7ED99C' }} />
                      <h3 className="text-lg font-semibold mb-2" style={{ color: '#6C4E78' }}>Interatividade total</h3>
                      <p className="text-sm" style={{ color: '#4A7C75' }}>Você movimenta seu avatar, representa e sente o campo</p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-md text-center">
                      <Shield className="h-8 w-8 mx-auto mb-4" style={{ color: '#7ED99C' }} />
                      <h3 className="text-lg font-semibold mb-2" style={{ color: '#6C4E78' }}>Consteladores homologados</h3>
                      <p className="text-sm" style={{ color: '#4A7C75' }}>Segurança e qualidade garantida</p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-md text-center">
                      <Heart className="h-8 w-8 mx-auto mb-4" style={{ color: '#7ED99C' }} />
                      <h3 className="text-lg font-semibold mb-2" style={{ color: '#6C4E78' }}>Vivência transformadora</h3>
                      <p className="text-sm" style={{ color: '#4A7C75' }}>Resolva questões pessoais ou aprenda mais sobre si mesmo</p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-md text-center">
                      <Users className="h-8 w-8 mx-auto mb-4" style={{ color: '#7ED99C' }} />
                      <h3 className="text-lg font-semibold mb-2" style={{ color: '#6C4E78' }}>Oportunidade de representar</h3>
                      <p className="text-sm" style={{ color: '#4A7C75' }}>Vivenciar e sentir o campo ajudando outras pessoas</p>
                    </div>
                  </div>
                </div>

                {/* FAQ - Só mostra se não for terapeuta */}
                <div className="mb-16" id="faq">
                  <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#6C4E78' }}>
                    Perguntas Frequentes
                  </h2>
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-md">
                      <h3 className="text-xl font-semibold mb-3" style={{ color: '#6C4E78' }}>
                        "Mas constelação online funciona?"
                      </h3>
                      <p className="text-base" style={{ color: '#4A7C75' }}>
                        Sim! A fenomenologia da constelação não se limita ao espaço físico. O campo morfogenético atua igualmente no presencial ou no virtual, e nossa plataforma garante posicionamento, representação e verbalizações dos representantes com a mesma profundidade.
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-md">
                      <h3 className="text-xl font-semibold mb-3" style={{ color: '#6C4E78' }}>
                        "Vou conseguir sentir a energia mesmo não estando no mesmo ambiente físico?"
                      </h3>
                      <p className="text-base" style={{ color: '#4A7C75' }}>
                        Sim! A experiência é real, intensa e muitas vezes mais confortável, pois você participa de um espaço seguro e acolhedor — onde quer que esteja.
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-md">
                      <h3 className="text-xl font-semibold mb-3" style={{ color: '#6C4E78' }}>
                        "Como vou interagir?"
                      </h3>
                      <p className="text-base" style={{ color: '#4A7C75' }}>
                        Por meio do seu avatar, que você movimenta livremente. Você também estará em vídeo e poderá se comunicar pelo chat ou verbalmente.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Benefícios para Terapeutas */}
          {shouldShowTherapistBenefits && (
            <section className="mb-20">
              <div className="max-w-6xl mx-auto">
                <div className="mb-16">
                  <div className="bg-gradient-to-br from-teal-50 to-purple-50 rounded-3xl p-8 md:p-12 shadow-lg">
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <ul className="space-y-4">
                          <li className="flex items-start gap-3">
                            <Globe className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#7ED99C' }} />
                            <div>
                              <span className="font-semibold" style={{ color: '#6C4E78' }}>Atenda clientes de qualquer lugar</span>
                              <p className="text-sm mt-1" style={{ color: '#4A7C75' }}>Sem limites geográficos</p>
                            </div>
                          </li>
                          <li className="flex items-start gap-3">
                            <Zap className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#7ED99C' }} />
                            <div>
                              <span className="font-semibold" style={{ color: '#6C4E78' }}>Sala virtual aconchegante</span>
                              <p className="text-sm mt-1" style={{ color: '#4A7C75' }}>Com avatares que se movimentam, fecham os olhos, sentam e representam no campo</p>
                            </div>
                          </li>
                          <li className="flex items-start gap-3">
                            <Shield className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#7ED99C' }} />
                            <div>
                              <span className="font-semibold" style={{ color: '#6C4E78' }}>Privacidade preservada</span>
                              <p className="text-sm mt-1" style={{ color: '#4A7C75' }}>Diálogo exclusivo com o constelado</p>
                            </div>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <ul className="space-y-4">
                          <li className="flex items-start gap-3">
                            <LayoutDashboard className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#7ED99C' }} />
                            <div>
                              <span className="font-semibold" style={{ color: '#6C4E78' }}>Gestão facilitada</span>
                              <p className="text-sm mt-1" style={{ color: '#4A7C75' }}>Agendamento e gestão tudo em um só lugar</p>
                            </div>
                          </li>
                          <li className="flex items-start gap-3">
                            <Star className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#7ED99C' }} />
                            <div>
                              <span className="font-semibold" style={{ color: '#6C4E78' }}>Visibilidade especial</span>
                              <p className="text-sm mt-1" style={{ color: '#4A7C75' }}>Se participar do Clube da Constelação, conte com visibilidade especial na plataforma para divulgação e captação de novos clientes</p>
                            </div>
                          </li>
                          <li className="flex items-start gap-3">
                            <Users className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#7ED99C' }} />
                            <div>
                              <span className="font-semibold" style={{ color: '#6C4E78' }}>Rede de representantes</span>
                              <p className="text-sm mt-1" style={{ color: '#4A7C75' }}>Disponível pelo Clube da Constelação</p>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {shouldShowSection(PlanSection.TERAPEUTA) && (
            <section className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#4A7C75' }}>Para Conduzir Sessões</h2>
              </div>
              {isLoadingPlans ? (
                <div className="text-center py-12">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" style={{ color: '#7ED99C' }}>
                    <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Carregando...</span>
                  </div>
                  <p className="mt-4" style={{ color: '#4A7C75' }}>Carregando planos...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {dynamicTherapistPlans.map((plan) => (
                    <PlanCard 
                      key={plan.id} 
                      plan={plan} 
                      onSelect={handlePlanSelection} 
                      isLoading={processingPlan === plan.id}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {shouldShowSection(PlanSection.PARTICIPANTE) && (
            <section>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#4A7C75' }}>Para Participar de Sessões</h2>
              </div>
              {isLoadingPlans ? (
                <div className="text-center py-12">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" style={{ color: '#7ED99C' }}>
                    <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Carregando...</span>
                  </div>
                  <p className="mt-4" style={{ color: '#4A7C75' }}>Carregando planos...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
                  {dynamicParticipantPlans.map((plan) => (
                    <PlanCard 
                      key={plan.id} 
                      plan={plan} 
                      onSelect={handlePlanSelection} 
                      isLoading={processingPlan === plan.id}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
      
      <InfoModal
        isOpen={modalState.isOpen}
        onClose={() => {
          setModalState({ ...modalState, isOpen: false });
          if (modalState.redirectTo) {
            navigate(modalState.redirectTo);
          }
        }}
        title={modalState.title}
        message={modalState.message}
        variant={modalState.variant}
        buttonText="Entendi"
      />
    </div>
  );
}